import { EtaTemplateCache } from "@/controllers/eta-renderer/eta-template-cache";
import { EtaRenderHost } from "@/controllers/eta-renderer/eta-types";

type ScheduleFn = () => void;

type Suspendable<T> = () => Promise<T> | T;

export class EtaMutationObserver {
  private host    : EtaRenderHost;
  private cache   : EtaTemplateCache;
  private schedule: ScheduleFn;

  private slotMo?   : MutationObserver;
  private shadowMos?: Map<ShadowRoot, MutationObserver>;
  private dirtyRoots = new Set<HTMLElement>();

  private debounce?     : number;
  private coalesceDelay?: number;

  private suspended = false;
  private sawMutationWhileSuspended = false;

  constructor(host: EtaRenderHost, cache: EtaTemplateCache, schedule: ScheduleFn) {
    this.host = host;
    this.cache = cache;
    this.schedule = schedule;
  }

  connect() {
    if (!this.host.shouldObserveEtaMutations()) return;
    if (this.slotMo) return;
    this.slotMo = new MutationObserver((muts) => this._handleMutations(muts));
    this.slotMo.observe(this.host, {
      childList    : true,
      subtree      : true,
      characterData: true,
      attributes   : true
    });
    this.host.querySelectorAll<HTMLElement>("[shadow]").forEach(host => {
      if (host.shadowRoot) this.ensureShadowRootObserved(host.shadowRoot, host);
    });
  }

  disconnect() {
    if (this.debounce) {
      clearTimeout(this.debounce);
      this.debounce = undefined;
    }
    this.slotMo?.disconnect();
    this.slotMo = undefined;
    if (this.shadowMos) {
      for (const [sr, mo] of this.shadowMos.entries()) {
        try { mo.disconnect(); } catch { /* ignore */ }
        try { this._cleanupShadowRoot(sr); } catch { /* ignore */ }
      }
      this.shadowMos.clear();
      this.shadowMos = undefined;
    }
  }

  isIdle(): boolean {
    return !this.debounce;
  }

  consumeDirtyRoots(): Set<HTMLElement> | undefined {
    if (this.dirtyRoots.size === 0) return undefined;
    const roots = new Set(this.dirtyRoots);
    this.dirtyRoots.clear();
    return roots;
  }

  async suspend<T>(fn: Suspendable<T>): Promise<T> {
    this.suspended = true;
    try {
      return await fn();
    } finally {
      this.suspended = false;
      if (this.sawMutationWhileSuspended) {
        this.sawMutationWhileSuspended = false;
        this._schedule();
      }
    }
  }

  ensureShadowRootObserved(sr: ShadowRoot, host?: HTMLElement) {
    if (!this.host.shouldObserveEtaMutations()) return;
    const rootHost = host ?? (sr.host instanceof HTMLElement ? sr.host : null);
    if (rootHost) {
      const eligible = this._findEligibleRoot(rootHost);
      if (!eligible) return;
    }
    if (!this.shadowMos) this.shadowMos = new Map();
    if (this.shadowMos.has(sr)) return;
    const mo = new MutationObserver((muts) => this._handleMutations(muts));
    mo.observe(sr, {
      childList    : true,
      subtree      : true,
      characterData: true,
      attributes   : true
    });
    this.shadowMos.set(sr, mo);
    sr.querySelectorAll<HTMLElement>("[shadow]").forEach(host => {
      if (host.shadowRoot) this.ensureShadowRootObserved(host.shadowRoot, host);
    });
  }

  private _schedule() {
    if (!this.host.shouldObserveEtaMutations()) return;

    const base = Math.max(0, Number(this.host.etaMutationDelay) || 0);
    const max  = Math.max(base, Number(this.host.etaMutationDelayMax) || 0);
    let delay  = base;
    if (this.debounce) {
      clearTimeout(this.debounce);
      const prev = this.coalesceDelay ?? base;
      delay = Math.min(prev > 0 ? prev * 2 : base, max);
    }
    this.coalesceDelay = delay;
    this.debounce = window.setTimeout(() => {
      this.debounce = undefined;
      this.coalesceDelay = base;
      this.schedule();
    }, delay);
  }

  private _handleMutations(muts: MutationRecord[]) {
    if (this.suspended) {
      this.sawMutationWhileSuspended = true;
      return;
    }
    for (const m of muts) {
      if (m.type === "attributes") {
        const attr = m.attributeName?.toLowerCase();
        if (attr === "class" || attr === "style") continue;
      }

      const targets: Node[] = [];
      if (m.type === "characterData" || m.type === "attributes") {
        targets.push(m.target);
        if (m.type === "attributes" && m.target instanceof HTMLElement) {
          this.cache.clearNoEtaAttrs(m.target);
        }
      } else {
        targets.push(...Array.from(m.addedNodes));
        for (const removed of Array.from(m.removedNodes)) {
          if (!(removed instanceof HTMLElement)) continue;
          if (removed.hasAttribute("shadow") && removed.shadowRoot) {
            this._cleanupShadowRoot(removed.shadowRoot);
          }
          removed
            .querySelectorAll<HTMLElement>("[shadow]")
            .forEach(host => host.shadowRoot && this._cleanupShadowRoot(host.shadowRoot));
        }
      }

      const eligibles: HTMLElement[] = [];
      for (const t of targets) {
        if (t instanceof HTMLElement && t.hasAttribute("shadow") && t.shadowRoot) {
          this.ensureShadowRootObserved(t.shadowRoot, t);
        }
        if (t instanceof HTMLElement) {
          t.querySelectorAll<HTMLElement>("[shadow]").forEach(host => {
            if (host.shadowRoot) this.ensureShadowRootObserved(host.shadowRoot, host);
          });
        }
        const root = this._findEligibleRoot(t);
        if (root) eligibles.push(root);
      }
      if (!eligibles.length) continue;

      eligibles.forEach(r => this.dirtyRoots.add(r));
      this._schedule();
      break;
    }
  }

  private _cleanupShadowRoot(sr: ShadowRoot) {
    if (this.shadowMos && this.shadowMos.has(sr)) {
      const mo = this.shadowMos.get(sr);
      try { mo?.disconnect(); } catch { /* ignore */ }
      this.shadowMos.delete(sr);
    }
    sr.querySelectorAll<HTMLElement>("[shadow]").forEach(host => {
      if (host.shadowRoot) this._cleanupShadowRoot(host.shadowRoot);
    });
  }

  private _findEligibleRoot(n: Node | null): HTMLElement | null {
    let cur: Node | null = n;
    while (cur && cur !== this.host) {
      if (cur instanceof ShadowRoot) {
        cur = cur.host;
        continue;
      }
      if (!(cur instanceof HTMLElement)) { cur = (cur as Node).parentNode; continue; }
      if (this.host.isStateNodeElement(cur) && cur !== this.host) return null;
      if (cur.hasAttribute("data-store-render")) return null;
      const parentProvider = cur.parentElement?.closest("bd-state,bd-product,bd-option,bd-context");
      if (parentProvider === this.host || cur.parentElement === this.host) return cur;
      cur = (cur as Node).parentNode;
    }
    return this.host as unknown as HTMLElement;
  }
}
